import { common, IOHelper, JSONHelper, } from '@neo-one/client-common';
import { BaseState } from '@neo-one/client-full-common';
import _ from 'lodash';
import { createSerializeWire, } from './Serializable';
import { BinaryReader, utils } from './utils';
export class Account extends BaseState {
    constructor({ version, hash, isFrozen = false, votes = [], balances = {} }) {
        super({ version });
        this.equals = utils.equals(Account, this, (other) => common.uInt160Equal(this.hash, other.hash));
        this.toKeyString = utils.toKeyString(Account, () => this.hashHex);
        this.serializeWire = createSerializeWire(this.serializeWireBase.bind(this));
        this.hash = hash;
        this.hashHex = common.uInt160ToHex(hash);
        this.isFrozen = isFrozen;
        this.votes = votes;
        this.balances = balances;
        this.sizeInternal = utils.lazy(() => IOHelper.sizeOfUInt8 +
            IOHelper.sizeOfUInt160 +
            IOHelper.sizeOfBoolean +
            IOHelper.sizeOfArray(this.votes, (vote) => IOHelper.sizeOfECPoint(vote)) +
            IOHelper.sizeOfObject(this.balances, () => IOHelper.sizeOfUInt256 + IOHelper.sizeOfFixed8));
    }
    static deserializeWireBase(options) {
        const { reader } = options;
        const version = reader.readUInt8();
        const hash = reader.readUInt160();
        const isFrozen = reader.readBoolean();
        const votes = reader.readArray(() => reader.readECPoint());
        const balances = reader.readObject(() => {
            const key = common.uInt256ToHex(reader.readUInt256());
            const value = reader.readFixed8();
            return { key, value };
        });
        return new Account({
            version,
            hash,
            isFrozen,
            votes,
            balances,
        });
    }
    static deserializeWire(options) {
        return this.deserializeWireBase({
            context: options.context,
            reader: new BinaryReader(options.buffer),
        });
    }
    get size() {
        return this.sizeInternal();
    }
    isDeletable() {
        const balances = Object.values(this.balances);
        return (!this.isFrozen &&
            this.votes.length === 0 &&
            (balances.length === 0 || balances.every((value) => value !== undefined && value.lte(utils.ZERO))));
    }
    getBalance(asset) {
        const balance = this.balances[asset];
        return balance === undefined ? utils.ZERO : balance;
    }
    update({ isFrozen = this.isFrozen, votes = this.votes, balances = this.balances }) {
        return new Account({
            hash: this.hash,
            isFrozen,
            votes,
            balances,
        });
    }
    serializeWireBase(writer) {
        writer.writeUInt8(this.version);
        writer.writeUInt160(this.hash);
        writer.writeBoolean(this.isFrozen);
        writer.writeArray(this.votes, (vote) => {
            writer.writeECPoint(vote);
        });
        const balances = _.pickBy(this.balances, (value) => value !== undefined && value.gt(utils.ZERO));
        writer.writeObject(balances, (key, value) => {
            writer.writeUInt256(common.stringToUInt256(key));
            writer.writeFixed8(value);
        });
    }
    async serializeJSON(context) {
        const [unspent, unclaimed] = await Promise.all([context.getUnspent(this.hash), context.getUnclaimed(this.hash)]);
        return {
            version: this.version,
            script_hash: JSONHelper.writeUInt160(this.hash),
            frozen: this.isFrozen,
            votes: this.votes.map((vote) => JSONHelper.writeECPoint(vote)),
            balances: Object.entries(this.balances).map(([asset, value]) => ({
                asset: JSONHelper.writeUInt256(asset),
                value: JSONHelper.writeFixed8(value),
            })),
            unspent: unspent.map((input) => input.serializeJSON(context)),
            unclaimed: unclaimed.map((input) => input.serializeJSON(context)),
        };
    }
}

//# sourceMappingURL=Account.js.map
